import React, { FC } from 'react'
import Box from '@src/components/Box'
import { makeStyles, Typography } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import DOMPurify from 'isomorphic-dompurify'

interface DiscoPromoContentProps {
  discount?: number | null
  qualifyingAmount?: number | null
  currency?: string | null
  validTo?: string | null
  disclaimer?: boolean | null
}

const useStyles = makeStyles(() => ({
  contentHeader: {
    letterSpacing: '0.14px',
    textTransform: 'capitalize',
  },
}))

const DiscoPromoContent: FC<DiscoPromoContentProps> = ({
  discount,
  qualifyingAmount,
  currency,
  validTo,
  disclaimer,
}) => {
  const classes = useStyles()
  const intl = useIntl()
  const discountValue = discount ?? 0
  const qualifyingValue = qualifyingAmount ?? 0
  const currencyValue = currency ?? 'USD'
  const validToDate = validTo != null ? new Date(validTo) : null
  const showDisclaimer = Boolean(disclaimer)

  const sanitizeHtml = (html?: string) => (html ? DOMPurify.sanitize(html) : '')

  if (qualifyingValue === 0 && discountValue === 0) return null

  const formattedQualifyingAmount = intl.formatNumber(qualifyingValue, {
    style: 'currency',
    currency: currencyValue,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })

  const formattedDiscount = intl.formatNumber(discountValue / 100, {
    style: 'percent',
    minimumFractionDigits: 0,
  })

  const promoContentDetails = validToDate
    ? intl.formatMessage(
        {
          id: 'SPEND_AND_SAVE_CONTENT_DETAILS',
          defaultMessage:
            'From now until {date}, stock up on your essentials.<br/>Add items to cart and click <strong>“Apply Discount” button in cart to redeem the savings*:</strong><br/>Spend {amount} — save {discount}*',
        },
        {
          date: intl.formatDate(validToDate, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
          amount: formattedQualifyingAmount,
          discount: formattedDiscount,
          strong: (chunks) => `<strong>${chunks}</strong>`,
          br: () => '<br/>',
        }
      )
    : intl.formatMessage(
        {
          id: 'SPEND_AND_SAVE_CONTENT_DETAILS_NO_EXPIRATION',
          defaultMessage:
            'Stock up on your essentials while supplies last.<br/>Add items to cart and click <strong>“Apply Discount” button in cart to redeem the savings*:</strong><br/>Spend {amount} — save {discount}*',
        },
        {
          amount: formattedQualifyingAmount,
          discount: formattedDiscount,
          strong: (chunks) => `<strong>${chunks}</strong>`,
          br: () => '<br/>',
        }
      )

  const asteriskDisclaimerDetails = validToDate
    ? intl.formatMessage(
        {
          id: 'SPEND_AND_SAVE_ASTERISK_DISCLAIMER',
          defaultMessage:
            '*Valid for web orders placed by {date}. This promotional discount only applies to products listed on the promotion page. Discount is calculated off of list price and cannot be combined with other offers. Exclusions and restrictions may apply. Purchase requirement is calculated before tax and shipping. Upon redemption of a promotion, organizational & contract pricing will override the promotion if lower. Your order will reflect the best eligible price.',
        },
        {
          date: intl.formatDate(validToDate, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
          }),
        }
      )
    : intl.formatMessage({
        id: 'SPEND_AND_SAVE_ASTERISK_DISCLAIMER_NO_EXPIRATION',
        defaultMessage:
          '*Valid for web orders. This promotional discount only applies to products listed on the promotion page. Discount is calculated off of list price and cannot be combined with other offers. Exclusions and restrictions may apply. Purchase requirement is calculated before tax and shipping. Upon redemption of a promotion, organizational & contract pricing will override the promotion if lower. Your order will reflect the best eligible price.',
      })

  return showDisclaimer ? (
    <Box mt={16}>
      <Typography
        component="p"
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(asteriskDisclaimerDetails),
        }}
      />
    </Box>
  ) : (
    <Box>
      <Typography component="h1" className={classes.contentHeader}>
        <FormattedMessage
          id="SPEND_AND_SAVE_BADGE"
          defaultMessage="Spend & Save"
        />
      </Typography>
      <Typography
        component="p"
        dangerouslySetInnerHTML={{ __html: sanitizeHtml(promoContentDetails) }}
      />
    </Box>
  )
}

export default DiscoPromoContent
